import {useCallback} from 'react'
import {CurrencyEnumIndices} from '@bdswiss/common-enums'
import {PaginationApi} from '../../../../../../shared/hooks/usePagination'
import {backendGetRequest} from '../../../../../../common/utils/requests'
import {getStartAndEndOfMonth} from '../utils'
import {useQuery, UseQueryResult} from './useQuery'

export type SubscriptionSettings = {
  strategy: {
    type: string
    direction: string
    fundsType: string
    riskRatio: number
    lotsStep: number
    minVolumeHandling: string
  }
  correction: {
    riskFactor: number | null
    action: string
  }
  riskRules: Array<{
    parameter: string
    threshold: number
    subscriptionAction: string
    tradingAction: string
    isLevelReached: boolean
  }>
}

export type SubscriptionStatistic = {
  id: number
  followerRemoteId: number
  followerAccountId: number
  followerFullName: string | null
  startedAt: string
  stoppedAt: string | null
  profit: number
  paidPerformanceFee: number
  pendingPerformanceFee: number
  currency: CurrencyEnumIndices | null
  settings: SubscriptionSettings | null
}

export type Data = {
  subscriptions: SubscriptionStatistic[]
  count: number
}

type Payload = {
  selectedDate: string
  paginationApi: PaginationApi
}

export function useSubscriptionStatistics(accountId: number, payload: Payload): UseQueryResult<Data> {
  const {selectedDate, paginationApi} = payload

  const query = useCallback(async (): Promise<Data> => {
    const {startsAt, endsAt} = getStartAndEndOfMonth(selectedDate)
    const {offset, limit} = paginationApi.value
    const params = {
      startsAt,
      endsAt,
      offset,
      limit
    }
    const response = await backendGetRequest(`/api/v2/dashboard/accounts/${accountId}/providers/brokeree/subscriptionStatistics`, true, params)

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to fetch provider statistics ${response.status} ${errorText}`)
    }

    return await response.json()
  }, [accountId, paginationApi.value, selectedDate])

  return useQuery(query)
}
